import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import debounce from 'lodash-es/debounce'

import { EMPTY_OBJECT } from '../../../constants/common'
import { EVENT_NAME_RESIZE } from '../../../constants/event'
import { FILE_SCOPE_EMAIL } from '../../../constants/file'
import { Counter } from '../../Counter'
import { EntityLabelList } from '../../Entity'
import { FileTableAttachments } from '../../File'
import { FloatingTooltip } from '../../Floating'
import { TableCellBase, TableCellNote } from '../../Table'
import Text from '../../Text'
import { getEmailMessageStatuses, isEmailHighPriority } from '../Email.utils'
import { EmailPriorityTag } from '../EmailPriorityTag'
import { MessageFolders } from '../MessageFolders'
import { MessageStatuses } from '../MessageStatuses'

import {
  FILES_VISIBLE_COUNT,
  SUBJECT_MARGIN_RIGHT,
  SUBJECT_OVERFLOW_GAP,
} from './EmailTableRowMessage.constants'
import { getNoteIcon } from './EmailTableRowMessage.utils'

import * as styles from './EmailTableRowMessage.module.css'

function EmailTableItemMessage({
  attachedFilesProps: {
    scope: attachedFilesScope = FILE_SCOPE_EMAIL,
    subject: attachedFilesSubject,
  } = EMPTY_OBJECT,
  className,
  floatingPortalId,
  isShort,
  item,
  scrollRef,
  singleEmailAccount,
  ...restProps
}) {
  const {
    labels,
    last_message: email,
    message_count: messageCount,
    notes,
    subject,
    unread,
  } = item

  const { completed, files, folders, text_preview: text } = email || item

  const { t } = useTranslation()

  const messageRef = useRef(null)
  const subjectRef = useRef(null)

  const [isSubjectOverflowed, setSubjectOverflowed] = useState(false)

  useEffect(() => {
    function checkSubjectOverflow() {
      // SUBJECT_MARGIN_RIGHT & SUBJECT_OVERFLOW_GAP used to prevent disabling tooltip
      // if only one letter of subject become ellipsis
      setSubjectOverflowed(
        subjectRef.current?.getBoundingClientRect().right +
          SUBJECT_MARGIN_RIGHT +
          SUBJECT_OVERFLOW_GAP >
          messageRef.current?.getBoundingClientRect().right
      )
    }

    checkSubjectOverflow()

    const debouncedCheck = debounce(checkSubjectOverflow, 50)

    window.addEventListener(EVENT_NAME_RESIZE, debouncedCheck)

    return () => {
      window.removeEventListener(EVENT_NAME_RESIZE, debouncedCheck)
    }
  }, [])

  //has_pending_message parameter used for support old api
  const statuses = getEmailMessageStatuses({
    error: item.last_error || item.error,
    sending: item.has_pending_message || completed === false,
    t,
  })

  return (
    <TableCellBase {...restProps} className={className} isShort={isShort}>
      <div className={styles.container}>
        <div className={styles.messageContainer}>
          <div className={styles.message} ref={messageRef}>
            {statuses?.length > 0 ? (
              <MessageStatuses statuses={statuses} />
            ) : null}
            {folders?.length > 0 ? (
              <MessageFolders items={folders} scrollRef={scrollRef} />
            ) : null}
            {subject || text ? (
              <FloatingTooltip
                content={subject}
                disabled={!subject || !isSubjectOverflowed}
              >
                <div className={styles.textContainer}>
                  {subject ? (
                    <Text
                      className={styles.subject}
                      ref={subjectRef}
                      unread={unread}
                    >
                      {subject}
                    </Text>
                  ) : null}
                  {!isShort && text ? (
                    <Text className={styles.text}>{text}</Text>
                  ) : null}
                </div>
              </FloatingTooltip>
            ) : (
              <Text className={styles.text}>
                {t('EmailTable.MessageEmptyKey')}
              </Text>
            )}
            {isEmailHighPriority(item) ? (
              <EmailPriorityTag className={styles.priority} />
            ) : null}
          </div>
          <EntityLabelList labels={labels} />
          {!isShort && files?.length > 0 ? (
            <FileTableAttachments
              files={files}
              floatingPortalId={floatingPortalId}
              scope={attachedFilesScope}
              scrollRef={scrollRef}
              subject={attachedFilesSubject}
              tooltipHintKey='EmailTable.MessageFilesItemTooltipHintKey'
              visibleCount={FILES_VISIBLE_COUNT}
            />
          ) : null}
          {!isShort && notes ? (
            <TableCellNote
              Icon={getNoteIcon(singleEmailAccount)}
              className={styles.notes}
              value={notes}
            />
          ) : null}
        </div>
        {messageCount ? (
          <Counter
            className={styles.messageCounter}
            count={messageCount}
            variant='table'
          />
        ) : null}
      </div>
    </TableCellBase>
  )
}

export default EmailTableItemMessage
